import React from 'react';
import ContextualLink from '@magoosh/gre/app/contextual_link';
import moment from 'moment-timezone';
import { paths } from 'config/path_helpers';
import { useUserContext } from '@magoosh/context/user_context';
import { StudyPlanItem } from '../../types';

import { DefaultButton } from '@magoosh/lib/button';
import StatusMessage from '@magoosh/gre/adaptive_prep_v2/lib/status_message';
import StudyItemCard from '@magoosh/gre/adaptive_prep_v2/organisms/study_item_card';

interface Props {
  isLoading: boolean;
  items: StudyPlanItem[];
}

export const CompletedTab: React.FC<Props> = (props) => {
  const timeZone =
    useUserContext().timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  const { isLoading, items } = props;

  // We use Moment's fromNow function to show a string like "4 days ago". By default the output
  // rounds up/down based on the decimal which means that the output is different before/after
  // 12pm each day. This ensures a set value for the entire day.
  moment.relativeTimeRounding(Math.floor);

  const groupItemsByDay = () => {
    const groupedItems = {};
    items.forEach((item) => {
      const utcCompletedAt = moment.utc(item.completedAt);
      const localCompletedAtDay = utcCompletedAt.tz(timeZone).format('YYYY-MM-DD');

      if (!groupedItems[localCompletedAtDay]) groupedItems[localCompletedAtDay] = [];
      groupedItems[localCompletedAtDay].push(item);
    });

    return groupedItems;
  };

  if (isLoading) {
    // TODO - appropriate status text
    return (
      <StatusMessage headline="How exciting!" isColored={false} text="Your study plan is almost ready..." />
    );
  } else if (!items || items.length === 0) {
    return (
      <StatusMessage
        headline="No Completed Activities to Display"
        text="When you complete activities from your To Do list, you’ll be able to see them here.">
        <ContextualLink href={paths.dashboard()}>
          <DefaultButton>View To Do</DefaultButton>
        </ContextualLink>
      </StatusMessage>
    );
  } else {
    const itemsGroupedByDay = groupItemsByDay();

    return (
      <>
        {items &&
          Object.keys(itemsGroupedByDay)
            .sort()
            .reverse()
            .map((dayString, dayIndex) => {
              const dayItems = itemsGroupedByDay[dayString].sort((a: StudyPlanItem, b: StudyPlanItem) => {
                return b.completedAt.localeCompare(a.completedAt);
              });

              return (
                <div key={dayString}>
                  {dayIndex !== 0 && <hr />}

                  <div className="u-margin-V-s">
                    <h4 className="u-margin-V-n">
                      <strong>{moment(dayString).format('dddd, MMMM D, YYYY')}</strong>
                    </h4>
                    {moment(dayString).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                      ? 'Today'
                      : moment(dayString).tz(timeZone).fromNow()}
                  </div>

                  {dayItems.map((item) => (
                    <StudyItemCard
                      key={item.id}
                      type={item.type}
                      section={item.section}
                      variant={item.type === 'Segment' || item.type === 'Lesson' ? 'secondary' : 'info'}
                      headline={item.headline}
                      subheadline={item.subheadline}
                      significanceLevel={item.significanceLevel}
                      minutes={item.estimatedTimeMinutes}
                      id={item.id}
                      date={item.completedAt}
                      shortDescription={item.shortDescription}
                      primaryCTA={{
                        ...item.primaryCta,
                        variant: item.primaryCta ? 'default' : ''
                      }}
                      isStrategy={item.isStrategy}
                      isCompleted={!!item.completedAt}
                    />
                  ))}
                </div>
              );
            })}
      </>
    );
  }
};

export default CompletedTab;
